import { z } from "zod";
/**
 * The manifest: the most load-bearing artifact in the framework.
 *
 * Authored in a connector package, validated here, stored as JSONB and seeded
 * from code. The admin UI, the policy shape, action discovery, audit action
 * names and the permitted `object_type` values are all GENERATED from it —
 * which is what stops each of those from needing provider-specific code.
 */
/** Lowercase snake segment: the alphabet every key in the manifest shares. */
const SEGMENT = /^[a-z][a-z0-9_]*$/;
const segment = z.string().regex(SEGMENT);
/**
 * The closed verb set.
 *
 * Not pedantry: UI strings, i18n keys, audit action names, metric labels and
 * RBAC privilege names are all derived from the verb, so an open set would
 * force each of those to be hand-written per provider. A provider needing a
 * seventh verb is a kernel change with a review — deliberately expensive.
 */
export const CAPABILITY_VERBS = [
    "create",
    "get",
    "list",
    "update",
    "comment",
    "search",
];
/** Verbs that mutate provider state, and therefore require approval. */
const WRITE_VERBS = new Set(["create", "update", "comment"]);
/** `<product>.<object>.<verb>` */
export const CapabilityKeySchema = z
    .string()
    .refine((value) => parseCapabilityKey(value) !== null, {
    message: "capability key must be <product>.<object>.<verb> with a verb from the closed set",
});
/** Returns null rather than throwing, so it doubles as the schema predicate. */
export function parseCapabilityKey(key) {
    const parts = key.split(".");
    if (parts.length !== 3)
        return null;
    const [product, object, verb] = parts;
    if (!SEGMENT.test(product) || !SEGMENT.test(object))
        return null;
    if (!CAPABILITY_VERBS.includes(verb))
        return null;
    return { product, object, verb: verb };
}
/**
 * The admin-facing settings form. Kept to a deliberately small JSON-Schema
 * subset: the kernel renders it, so an unbounded schema would mean an
 * unbounded renderer.
 */
export const SettingsSchema = z
    .object({
    type: z.literal("object"),
    required: z.array(z.string()).optional(),
    properties: z.record(z.object({
        type: z.enum(["string", "number", "boolean"]),
        title: z.string(),
        description: z.string().optional(),
        format: z.enum(["uri", "email", "secret"]).optional(),
        enum: z.array(z.string()).optional(),
        /**
         * The connector declaring "this field legitimately holds a high-entropy
         * value that is PUBLIC, so do not treat it as a pasted credential."
         *
         * Exists because the two rules the core applies to `settings` disagree on
         * exactly one shape. `containsSecretMaterial` flags any unbroken 24+
         * character opaque run, which is what a pasted token looks like — and also
         * what an OAuth `client_id` looks like. A real one is 32 alphanumeric
         * characters, so before this flag every realistic client id was refused
         * with "looks like credential material" and no live OAuth integration
         * could be created at all. Every fixture in the tree used a short
         * hand-written value (`qa-client`), so nothing caught it.
         *
         * Only the connector can settle this: whether an opaque value is public is
         * a fact about the provider's protocol, not something the core can infer
         * from the string. Hence a port rather than a special case for a
         * well-known key name (ADR-0042) — the core still never learns why a
         * particular field is exempt.
         *
         * Deliberately NARROW. It suppresses only the entropy heuristic; the
         * declared-key check, the type check, and the outright refusal of
         * `format: "secret"` all still apply. A field cannot be both
         * `format: "secret"` and public — the manifest parser rejects that pair.
         */
        publicIdentifier: z.boolean().optional(),
        /**
         * The connector declaring "this field is no longer used; existing rows
         * may still carry it."
         *
         * Retiring a setting has two audiences that want opposite things. The
         * ADMIN FORM should stop offering it the moment it stops meaning
         * anything — a box whose value is ignored is worse than no box.
         * VALIDATION must keep accepting it, because every instance configured
         * before the change still has the key in `settings`, and an undeclared
         * key is a violation — so deleting the property outright turns the next
         * unrelated edit of a working integration into a 422.
         *
         * Hence declared-but-hidden rather than deleted. The property is
         * removed for real once no stored row carries it, which is a data
         * question and therefore a deployment decision, not a code one.
         *
         * Introduced by PB-4734 for `clientId`: the OAuth application moved to
         * the platform, so the value is now configuration rather than tenant
         * settings. Provider-neutral, like every other flag here — the core
         * still never learns WHY a field was retired.
         */
        deprecated: z.boolean().optional(),
        /**
         * The connector declaring "changing this setting changes WHICH tenant of
         * the provider this integration authorizes against."
         *
         * ## The drift this closes
         *
         * A connection records the provider tenant it was authorized for, and
         * the core refuses a connection whose tenant differs from the
         * instance's pin. But the pin an administrator actually configures is a
         * SETTING — a site URL, an org slug, a workspace host — and the
         * instance's `externalTenantId` column is optional. So an administrator
         * could edit that setting to name a different tenant, leave the column
         * null, and every connection authorized under the OLD value kept
         * working: nothing compared the two, because nothing recorded which
         * value each connection was authorized under.
         *
         * Flagging the setting makes the core fingerprint it at authorization
         * and re-derive the fingerprint on every use. Editing a flagged setting
         * therefore invalidates every existing connection on that instance
         * until it is re-authorized — sync, effective access, governed tool
         * calls, resource listing, reads and writes all refuse.
         *
         * ## Provider-neutral, and narrow
         *
         * The core never learns WHY a field is a boundary, only that it is; the
         * fingerprint is a digest of the flagged values and is never parsed.
         * Flag only settings that select a tenant. A display name or a page-size
         * preference is not one, and flagging it would make an unrelated edit
         * log every user out of the integration.
         *
         * A connector that flags nothing is unaffected: its fingerprint is
         * constant, so no edit can invalidate a connection.
         */
        authorizationBoundary: z.boolean().optional(),
    })),
})
    .superRefine((schema, ctx) => {
    for (const [key, property] of Object.entries(schema.properties)) {
        if (property.format === "secret" && property.publicIdentifier === true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["properties", key],
                message: `setting "${key}" cannot be both format: "secret" and publicIdentifier: true`,
            });
        }
    }
    // A DEPRECATED setting cannot also be REQUIRED: required forces every new
    // instance to supply a value the connector says nobody should supply any
    // more, and the two flags pull the admin form in opposite directions. The
    // migration path (PB-4734's clientId is the worked example) is deprecated
    // AND optional — old instances still validate, new ones stop asking.
    for (const [index, key] of (schema.required ?? []).entries()) {
        if (schema.properties[key]?.deprecated === true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["required", index],
                message: `setting "${key}" is deprecated and cannot be required`,
            });
        }
    }
});
export const ResourceTypeSchema = z.object({
    key: segment,
    displayName: z.string().min(1),
    /** Hierarchy: a folder is listed *within* a container. Null = top level. */
    parentResourceType: z.string().nullable(),
    selectorLabel: z.string().min(1),
});
export const CapabilitySchema = z.object({
    key: CapabilityKeySchema,
    displayName: z.string().min(1),
    kind: z.enum(["read", "write"]),
    /** What lands in `link.external_work_refs.object_type` (PB-513). */
    objectType: segment,
    /** Which resource type scopes it — must be declared by the same product. */
    resourceType: segment,
    requiresApproval: z.boolean(),
    form: z.enum(["static", "dynamic"]),
    /**
     * Declared when the provider CANNOT be probed after an ambiguous write. The
     * engine then refuses to retry and routes to a human, because a blind retry
     * is how duplicates happen.
     */
    singleAttempt: z.boolean().default(false),
    /** Neutral SourceSnapshot key -> provider field name. */
    defaultFieldMap: z.record(z.string()).default({}),
    /**
     * Which of this capability's INPUT FIELDS an administrator may restrict to a
     * list of allowed values, via `access_policy.capabilityInputs` (PB-4734).
     *
     * The gap it closes: policy could restrict WHERE an action lands — the
     * resource — and nothing about WHAT it creates there. An administrator could
     * allow one Jira project and not restrict the issue types created in it.
     *
     * ## Why the manifest declares this rather than the core knowing it
     *
     * A field is constrainable when its values form a CLOSED SET the provider
     * enumerates — an issue type, a content type, a label from a fixed
     * vocabulary. Free text is not, and neither is anything the provider mints
     * per request. Only the connector knows which of its fields are which, so the
     * declaration lives here and the core never learns a field's name (ADR-0042).
     * Same shape as `resourceTypes`: the manifest says what CAN be restricted, the
     * policy says what IS.
     *
     * ## Deliberately narrow (open decision D4)
     *
     * This is "allowed values for a declared field", not a rules language. No
     * operators, no ranges, no cross-field conditions, no negation — a policy
     * entry is a list of permitted strings and nothing else. The kernel's public
     * types are frozen (PB-2591), so the surface added here is the smallest that
     * answers the requirement; widening it later is additive, and shipping a
     * predicate language now would not be reversible.
     *
     * Field keys, not paths: a constrainable field is a top-level input field the
     * form collects, so nested addressing is not expressible and does not need to
     * be. Empty default, so every existing manifest keeps its current meaning —
     * nothing constrainable, therefore nothing constrained.
     */
    constrainableInputs: z.array(z.string().min(1)).default([]),
});
export const ProductSchema = z.object({
    key: segment,
    displayName: z.string().min(1),
    resourceTypes: z.array(ResourceTypeSchema),
    capabilities: z.array(CapabilitySchema),
});
export const RateLimitSchema = z.object({
    requestsPerMinute: z.number().int().positive(),
    burst: z.number().int().positive(),
});
/**
 * Rate limits live in the MANIFEST (per template), with an optional per-instance
 * override in `tenant_integrations.settings` — the template knows the provider's
 * published limits, while a tenant may need to be more conservative on a shared
 * account. The kernel only defines the shape; EI-05 applies the override.
 */
/**
 * Which of THIS provider's status categories mean finished, and which mean the
 * work is still live (PB-531).
 *
 * The problem it solves: `external-ref-status.resolver.ts` held one hardcoded
 * table — `done` plus `{new, indeterminate}` — and applied it to EVERY external
 * reference, keyed on the ref's KIND rather than on its provider. Those are
 * Jira's three values. A GitHub or SharePoint reference routed through it would
 * have been classified with Jira's vocabulary: the `switch (providerKey)`
 * anti-pattern in latent form, already in the core, waiting for provider two.
 *
 * ## Why this is DATA a connector declares, not a mapping it performs
 *
 * The kernel is explicit that a connector must not map into the platform's
 * buckets — `StatusReader` returns `providerStatusCategory` verbatim, because
 * two connectors that each decided what "done" means would eventually disagree
 * and a rollup would count one provider's work differently from another's.
 *
 * This does not breach that rule, and the distinction is the point. A connector
 * LISTS its provider's own vocabulary — a fact only it knows — and the platform
 * decides what those values imply. Nobody else can enumerate Jira's
 * `statusCategory` keys; nobody but the platform should decide that "finished"
 * belongs in the done bucket.
 *
 * OPTIONAL, and absence is meaningful rather than lenient: a provider with no
 * declared vocabulary classifies as `unavailable`, never as `open`. Guessing
 * `open` would put work in a rollup's denominator on no evidence.
 */
const StatusVocabularySchema = z
    .object({
    /** Categories meaning the work is finished. */
    done: z.array(z.string().min(1)).default([]),
    /** Categories meaning it is still live — including "not started". */
    open: z.array(z.string().min(1)).default([]),
})
    .strict()
    .superRefine((vocabulary, ctx) => {
    // A category in both buckets has no defined meaning, and silently
    // preferring one would make a rollup depend on evaluation order.
    const overlap = vocabulary.done.filter((c) => vocabulary.open.includes(c));
    if (overlap.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `status categories cannot be both done and open: ${overlap.join(", ")}`,
        });
    }
});
export const ConnectorManifestSchema = z
    .object({
    templateKey: segment,
    providerKey: segment,
    displayName: z.string().min(1).max(200),
    version: z.number().int().positive(),
    protocol: z.enum(["mcp", "rest"]),
    authModes: z
        .array(z.enum([
        "delegated_oauth",
        "tenant_shared",
        "service_account",
        "app_installation",
    ]))
        .nonempty(),
    settingsSchema: SettingsSchema,
    // Pure MCP connectors discover their tools remotely and therefore need no
    // placeholder static products, resources or capabilities.
    products: z.array(ProductSchema),
    rateLimit: RateLimitSchema,
    statusVocabulary: StatusVocabularySchema.optional(),
})
    .superRefine((manifest, ctx) => {
    if (manifest.protocol === "rest" && manifest.products.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["products"],
            message: "a REST manifest must declare at least one product",
        });
    }
    const productKeys = new Set(manifest.products.map((p) => p.key));
    const seenCapabilityKeys = new Set();
    for (const product of manifest.products) {
        const resourceTypeKeys = new Set(product.resourceTypes.map((r) => r.key));
        for (const resourceType of product.resourceTypes) {
            // A hierarchy that points outside its own product is unresolvable by
            // the generic resource lister, so reject it at authoring time.
            if (resourceType.parentResourceType !== null &&
                !resourceTypeKeys.has(resourceType.parentResourceType)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["products", product.key, "resourceTypes", resourceType.key],
                    message: `parentResourceType "${resourceType.parentResourceType}" is not declared by product "${product.key}"`,
                });
            }
        }
        for (const capability of product.capabilities) {
            const parsed = parseCapabilityKey(capability.key);
            if (parsed === null)
                continue; // already reported by the key schema
            if (seenCapabilityKeys.has(capability.key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["products", product.key, "capabilities", capability.key],
                    message: `duplicate capability key "${capability.key}"`,
                });
            }
            seenCapabilityKeys.add(capability.key);
            // The key's product prefix must be a product this manifest declares —
            // otherwise generated UI and policy paths point at nothing.
            if (!productKeys.has(parsed.product)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["products", product.key, "capabilities", capability.key],
                    message: `capability key product "${parsed.product}" is not a declared product`,
                });
            }
            if (parsed.product !== product.key) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["products", product.key, "capabilities", capability.key],
                    message: `capability "${capability.key}" is declared under product "${product.key}"`,
                });
            }
            // The verb and the declared kind must agree, or approval policy is
            // decided by whichever of the two a given call site happened to read.
            const verbWrites = WRITE_VERBS.has(parsed.verb);
            if (verbWrites && capability.kind !== "write") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["products", product.key, "capabilities", capability.key],
                    message: `verb "${parsed.verb}" mutates, so kind must be "write"`,
                });
            }
            if (!verbWrites && capability.kind === "write") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["products", product.key, "capabilities", capability.key],
                    message: `verb "${parsed.verb}" is read-only, so kind must be "read"`,
                });
            }
            // Ground rule #2, enforced at authoring time rather than at execution.
            if (capability.kind === "write" && !capability.requiresApproval) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["products", product.key, "capabilities", capability.key],
                    message: `write capability "${capability.key}" must set requiresApproval: true`,
                });
            }
            if (!resourceTypeKeys.has(capability.resourceType)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["products", product.key, "capabilities", capability.key],
                    message: `resourceType "${capability.resourceType}" is not declared by product "${product.key}"`,
                });
            }
        }
    }
});
/** Throws a zod error with every problem, not just the first. */
export function parseManifest(input) {
    return ConnectorManifestSchema.parse(input);
}
/** Flat view of every capability, which is what the engine actually indexes. */
export function manifestCapabilities(manifest) {
    return manifest.products.flatMap((product) => product.capabilities);
}
//# sourceMappingURL=manifest.js.map